import { useEffect, useRef, useState } from 'react';
import { ShieldIcon } from '@wyocspay/ui';
import type { PaymentView } from './api.js';
import type { Strings } from './i18n.js';

/**
 * The security note in the page chrome, and the panel it opens.
 *
 * The brief for this was a badge saying the platform is checked regularly and
 * meets the standards banks hold it to. What is drawn instead is one quiet line
 * in the footer, because a payment page is the last place a loud reassurance
 * belongs: a seal shouting about security next to a card field is the visual
 * grammar of every phishing page ever built, and a customer who has to be told
 * emphatically that a page is safe has already been given a reason to wonder.
 * The line reads as part of the footer at a glance and opens into specifics for
 * anyone who goes looking, which is the audience that actually wanted it.
 *
 * Four constraints shaped the implementation, and none of them are preferences.
 *
 * 1. Every pixel here is drawn by us. No third-party seal, no vendor badge, no
 *    external script, image, font or iframe of any kind. The page's
 *    Content-Security-Policy is `default-src 'none'` with Stripe added back,
 *    and that policy is what keeps this platform inside PCI DSS v4.0.1 SAQ A -
 *    a trust seal fetched from a security vendor's CDN would be a third-party
 *    request from the payment page, and would cost exactly what section 5 of
 *    compliance/payment-page-advertising.md says it costs. The glyph is the
 *    inline SVG the rest of the page already uses and the panel is CSS.
 *
 * 2. Nothing here claims more than is true. Scanning is quarterly, so the copy
 *    says every three months and never "continuously", "24/7" or "real-time".
 *    There is no "bank-grade" or "military-grade" anything, because those
 *    phrases describe nothing. Stripe is named in words only: their marks carry
 *    brand guidelines and this platform is not going to guess at them.
 *
 * 3. The scan report is never published, linked or excerpted. An ASV report
 *    lists services, versions and findings against a live host. The only things
 *    that reach this component are the date of the most recent passing scan and
 *    the standard string, and even those come from platform settings rather
 *    than from here.
 *
 * 4. It cannot reach the payment form. The resting lockup lives in the footer,
 *    below the main element, in normal flow with no positioning and no z-index,
 *    so there is no route by which it can overlay or obscure the Stripe
 *    element. The panel does cover the page while it is open, but only because
 *    somebody deliberately opened it, and it is the same native <dialog> the
 *    legal documents already open over the checkout.
 */
export function Assurance({
  t,
  locale,
  assurance,
}: {
  t: Strings;
  locale: string;
  assurance: PaymentView['assurance'];
}) {
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDialogElement>(null);

  /**
   * Native <dialog>, opened modally, for the reason LegalModal uses it: focus
   * trapping, Escape to close and inertness of the page behind it, none of it
   * reimplemented and none of it reimplemented badly. Two dialogs behaving
   * differently on the same page would be worse than either behaving oddly.
   */
  useEffect(() => {
    const dialog = panel.current;
    if (open && dialog && !dialog.open) dialog.showModal();
  }, [open]);

  /**
   * Every way out goes through the element's own `close()`.
   *
   * The close control and a click on the backdrop call it rather than
   * unmounting the dialog directly, so there is one close path and the browser
   * runs its own teardown on all of them, Escape included. `onClose` is then
   * the single place that puts the component back to rest.
   *
   * The explicit focus call is belt and braces. The specification already says
   * a closing dialog returns focus to whatever had it before, and browsers do,
   * but a keyboard user who ends up with focus on <body> has lost their place
   * on a page they were part way through paying on. One line is a cheap
   * guarantee.
   */
  function dismissed(): void {
    setOpen(false);
    trigger.current?.focus();
  }

  /**
   * A standard that is blank, or is nothing but spaces, is no standard.
   *
   * The server already sends null for an unset value, so this is the case where
   * somebody typed whitespace into the admin console and saved it. Either way
   * the row is not drawn, because an empty monospace box under a heading
   * reading "Assessment" says something worse than nothing at all.
   */
  const standard = assurance.standard?.trim() ? assurance.standard.trim() : null;

  /**
   * The date, formatted in the customer's language and read as UTC.
   *
   * The time zone is not a detail here. `2026-07-14` parses as midnight UTC,
   * and formatting that in the browser's own zone shows 13 July to every payer
   * west of Greenwich - a compliance note that reports a different date
   * depending on where it is read is worse than one that reports none. Pinning
   * the calculation to UTC makes the published date the same everywhere.
   *
   * A value that will not parse produces null and the line is not drawn. The
   * server already refuses anything that is not a real calendar date in the
   * past, so this should never fire; it is here because the failure it guards
   * against is a confident, wrong date about security shown to a cardholder,
   * and one validation is not enough of them for that.
   */
  const scanDate = formatDay(assurance.last_scan_date, locale);

  return (
    <>
      <button
        ref={trigger}
        type="button"
        className="wc-assurance"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(true)}
      >
        <ShieldIcon size={13} />
        <span className="wc-assurance-label">{t.assuranceLabel}</span>
      </button>

      {open ? (
        <dialog
          ref={panel}
          className="wc-assurance-panel"
          onClose={dismissed}
          onClick={(e) => {
            // The dialog element reports a backdrop click as a click on itself,
            // so the target is what separates "outside" from "inside".
            if (e.target === panel.current) panel.current?.close();
          }}
          aria-labelledby="wc-assurance-title"
        >
          <header className="wc-assurance-head">
            <h2 id="wc-assurance-title">
              <ShieldIcon size={14} />
              {t.assuranceLabel}
            </h2>
            <button
              type="button"
              className="wc-assurance-close"
              aria-label={t.assuranceClose}
              onClick={() => panel.current?.close()}
            >
              &times;
            </button>
          </header>

          <div className="wc-assurance-body">
            {/*
              Card handling first, because it is the strongest thing this page
              can say and the only one that is structural rather than a matter
              of process. The footer already states it from the platform's side;
              this states it from the customer's, which is what somebody who
              opened a panel headed "security" came here to read.
            */}
            <Row head={t.assuranceCardsHead}>
              <p>{t.assuranceCards}</p>
            </Row>

            {standard ? (
              <Row head={t.assuranceStandardHead}>
                {/*
                  A version string is an identifier, not prose, so it is set as
                  one: monospace, tabular, in a box of its own. It is also the
                  part of this panel most likely to be copied into an email to
                  somebody's compliance officer.
                */}
                <p className="wc-num wc-assurance-token">{standard}</p>
                <p>{t.assuranceStandardNote}</p>
              </Row>
            ) : null}

            {/*
              The scan line disappears entirely when no passing scan has been
              recorded, rather than showing a placeholder, an assumed quarter or
              the sentence on its own. The claim is that the most recent scan
              passed on a particular date, and without the date there is no
              claim to make.
            */}
            {scanDate ? (
              <Row head={t.assuranceScanHead}>
                <p>{t.assuranceScan}</p>
                <p className="wc-assurance-fact">
                  <span>{t.assuranceScanLast}</span>
                  <span className="wc-num">{scanDate}</span>
                </p>
              </Row>
            ) : null}

            <Row head={t.assuranceTlsHead}>
              <p>{t.assuranceTls}</p>
            </Row>
          </div>
        </dialog>
      ) : null}

      <style>{`
        /*
          The resting state.

          Twelve pixels in the faintest ink on the page, which is the same
          treatment the footer's own small print gets - somebody scanning past
          it reads it as part of the chrome. The hairline under the label is
          the only thing that says it can be opened, and it is deliberately a
          rule rather than an underline: this page is built out of hairlines,
          and one more belongs.

          The 44px minimum height is the touch target. The visible lockup is
          about seventeen pixels tall, so the button carries the rest as empty
          space around it, which costs the footer a few pixels of height and
          gives a thumb something to hit. Height rather than padding and a
          positioned overlay, because nothing on this page needs another
          absolutely positioned box.
        */
        .wc-assurance {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          min-height: 44px;
          margin: 0;
          padding: 0;
          border: none;
          background: none;
          color: var(--wc-faint);
          font: inherit;
          font-size: 12px;
          line-height: 1.45;
          text-align: left;
          cursor: pointer;
          transition: color 180ms ease-out;
        }

        .wc-assurance-label {
          padding-bottom: 2px;
          border-bottom: 1px solid var(--wc-border);
          transition: border-color 180ms ease-out;
        }

        .wc-assurance:hover,
        .wc-assurance:focus-visible {
          color: var(--wc-muted);
        }

        .wc-assurance:hover .wc-assurance-label,
        .wc-assurance:focus-visible .wc-assurance-label {
          border-bottom-color: var(--wc-border-strong);
        }

        /*
          The panel. Narrow on purpose: four short facts read better in a column
          than spread across a dialog the width of the legal documents, and a
          420px box at 375px viewport width still leaves a margin on both sides.
        */
        .wc-assurance-panel {
          width: min(420px, calc(100vw - 32px));
          max-height: min(82vh, 680px);
          padding: 0;
          border: 1px solid var(--wc-border-strong);
          background: var(--wc-surface);
          color: var(--wc-body);
          box-shadow: 0 12px 48px rgba(38, 49, 58, 0.28);
          animation: wc-assurance-rise 180ms ease-out;
        }

        .wc-assurance-panel::backdrop {
          background: rgba(26, 35, 42, 0.5);
          animation: wc-assurance-fade 180ms ease-out;
        }

        @keyframes wc-assurance-rise {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: none; }
        }

        @keyframes wc-assurance-fade {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        /*
          The global token stylesheet already flattens every animation and
          transition to nothing under this query, so this block changes no
          behaviour. It is written out anyway, beside the animations it governs,
          so that nobody moving this component into another stylesheet takes the
          motion with it and leaves the exemption behind.
        */
        @media (prefers-reduced-motion: reduce) {
          .wc-assurance,
          .wc-assurance-label { transition: none; }
          .wc-assurance-panel,
          .wc-assurance-panel::backdrop { animation: none; }
        }

        /*
          The padding lives on the title and on the button rather than on the
          band, so the band's height is set by the 44px control inside it. A
          padded header with a 44px button in it is a 64px header, which is a
          lot of chrome above four short facts.
        */
        .wc-assurance-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding: 0 4px 0 18px;
          border-bottom: 1px solid var(--wc-border);
          background: var(--wc-surface-alt);
        }

        .wc-assurance-head h2 {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 14px 0;
          font-size: 13px;
          font-weight: 700;
          letter-spacing: 0.05em;
          text-transform: uppercase;
          color: var(--wc-blue-ink);
        }

        /*
          Small mark, large target. The square is 44px so a thumb can find it,
          and it carries no border at rest so it does not become the heaviest
          thing in a panel whose whole argument is restraint. The tint on hover
          is what says it is a control; the border it would otherwise need would
          be saying that permanently, to everybody, including the people who are
          reading rather than closing.
        */
        .wc-assurance-close {
          flex: none;
          width: 44px;
          height: 44px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: none;
          background: none;
          color: var(--wc-faint);
          font-size: 20px;
          line-height: 1;
          cursor: pointer;
          transition: color 180ms ease-out, background 180ms ease-out;
        }

        .wc-assurance-close:hover {
          color: var(--wc-ink);
          background: var(--wc-blue-tint);
        }

        .wc-assurance-body {
          overflow-y: auto;
          padding: 4px 18px 18px;
        }

        /* One hairline between facts and nothing else. The whitespace does the
           separating; the rules only say where one fact ends. */
        .wc-assurance-row {
          padding: 16px 0;
          border-bottom: 1px solid var(--wc-border);
        }

        .wc-assurance-row:last-child {
          border-bottom: none;
          padding-bottom: 2px;
        }

        .wc-assurance-row h3 {
          margin-bottom: 7px;
          color: var(--wc-muted);
          font-size: 11.5px;
          font-weight: 600;
          letter-spacing: 0.06em;
          text-transform: uppercase;
        }

        .wc-assurance-row p {
          margin: 0;
          font-size: 13px;
          line-height: 1.55;
          color: var(--wc-body);
        }

        .wc-assurance-row p + p {
          margin-top: 9px;
        }

        /* Written against the row rather than on their own, so these beat the
           paragraph rule above on specificity instead of on !important. */
        .wc-assurance-row p.wc-assurance-token {
          display: inline-block;
          padding: 3px 8px;
          border: 1px solid var(--wc-border);
          background: var(--wc-surface-alt);
          color: var(--wc-ink);
          font-size: 12.5px;
          letter-spacing: 0.01em;
        }

        /* Label left, value right, sharing a baseline - the same shape the
           receipt uses for an amount, because it is the same kind of statement. */
        .wc-assurance-row p.wc-assurance-fact {
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          gap: 12px;
          font-size: 12.5px;
          color: var(--wc-muted);
        }

        .wc-assurance-fact .wc-num {
          color: var(--wc-ink);
        }
      `}</style>
    </>
  );
}

function Row({ head, children }: { head: string; children: React.ReactNode }) {
  return (
    <section className="wc-assurance-row">
      <h3>{head}</h3>
      {children}
    </section>
  );
}

/**
 * `YYYY-MM-DD` to a date in the reader's language, or null if it is not one.
 *
 * Pinned to UTC so the published date does not shift by a day depending on
 * where it is read, and set in a medium style so every locale renders something
 * unambiguous rather than a numeric order that means two different dates on two
 * sides of an ocean.
 */
function formatDay(day: string | null, locale: string): string | null {
  if (!day) return null;
  const at = Date.parse(`${day}T00:00:00Z`);
  if (Number.isNaN(at)) return null;
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeZone: 'UTC' }).format(at);
  } catch {
    return null;
  }
}
